import { BrandMark, BrandWordmark } from "@manos/blocks";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { api, apiUrl, meQuery, providersQuery } from "../lib/api";

const ERRORS: Record<string, string> = {
  "not-invited": "That Google account hasn't been invited to Manos Studio. Ask an admin to add your email.",
  expired: "The sign-in took too long. Please try again.",
  cancelled: "Sign-in was cancelled.",
  unverified: "Your Google email address isn't verified.",
  google: "Google sign-in failed. Please try again.",
  "google-not-configured": "Google sign-in isn't set up yet. It's connected once the GCP project exists.",
};

export function Login() {
  const { error } = useSearch({ from: "/login" });
  const providers = useQuery(providersQuery);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const dev = useMutation({
    mutationFn: () => api("/auth/dev-login", { method: "POST", json: { email } }),
    onSuccess: async () => {
      // Fetch (not just invalidate): nothing observes this query yet, so invalidating wouldn't refetch,
      // and the route guard would read the cached "signed out" and bounce back here.
      await qc.fetchQuery({ ...meQuery, staleTime: 0 });
      await navigate({ to: "/pages" });
    },
  });

  return (
    <div className="login">
      <div className="login-card">
        <BrandMark className="login-mark" />
        <div className="brand lg">
          <BrandWordmark />
          <span>Studio</span>
        </div>
        <p className="muted">Sign in to manage the work, pages and media on manosedit.com.</p>
        {error && (
          <p className="alert" role="alert">
            {ERRORS[error] ?? "Sign-in failed. Please try again."}
          </p>
        )}

        {providers.data?.google !== false && (
          <a className="btn primary block" href={apiUrl("/auth/google")}>
            Continue with Google
          </a>
        )}

        {providers.data?.dev && (
          <form
            className="dev-login"
            onSubmit={(e) => {
              e.preventDefault();
              dev.mutate();
            }}
          >
            <span className="eyebrow">Local development only</span>
            <label htmlFor="dev-email">Sign in as</label>
            <div className="row">
              <input
                id="dev-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
              />
              <button type="submit" className="btn" disabled={dev.isPending}>
                {dev.isPending ? "Signing in…" : "Sign in"}
              </button>
            </div>
            {dev.error && <p className="field-err">{dev.error.message}</p>}
          </form>
        )}
      </div>
    </div>
  );
}
